import type { ContractInvoice, Invoice } from "@/types/invoice"

/**
 * One entry of GET /customers/contracts/{id}/invoices as the app's Invoice. `entityId`
 * is reconstructed for code that still reads it (the line for rack/insurance
 * invoices, the contract otherwise); what a contract card relies on is the
 * server-resolved attribution and basket fields kept alongside.
 */
export function toInvoice(item: ContractInvoice): Invoice {
  return {
    id: item.id,
    customerId: item.customerId,
    entityType: item.entityType,
    entityId: item.lineId ?? item.contractId,
    contractId: item.contractId,
    lineId: item.lineId,
    kind: item.kind,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    amount: item.amount,
    quantity: item.quantity,
    total: item.total,
    direction: item.direction,
    payable: item.payable,
    name: item.name,
    paidAt: item.paidAt,
    from: item.from,
    to: item.to,
    status: item.status,
  }
}

/**
 * Whether an invoice can go into a payment basket — the API's `payable`: pending and
 * not a refund. A pending refund is money owed to the customer and checkout rejects it.
 */
export function isPayable(invoice: Invoice): boolean {
  return invoice.payable ?? invoice.status === "pending"
}

export function payableInvoices(invoices: Invoice[]): Invoice[] {
  return invoices.filter(isPayable)
}

/** What a basket costs: the sum of each invoice's `total` (amount × quantity), in kopecks precision. */
export function invoicesTotal(invoices: Invoice[]): number {
  const kopecks = invoices.reduce(
    (sum, invoice) => sum + Math.round((invoice.total ?? invoice.amount ?? 0) * 100),
    0,
  )
  return kopecks / 100
}

/** The box rent itself: not a penalty (`fee`), not a line's (rack, insurance) invoice, not a refund. */
export function isRentInvoice(invoice: Invoice): boolean {
  return invoice.kind === "rent" && invoice.lineId == null && invoice.direction !== "refund"
}

/**
 * The period a contract card names for its box rent: what the unpaid rent invoices
 * cover, or, when everything is paid, the last paid one.
 */
export function rentInvoicePeriod(invoices: Invoice[]): { from: string; to: string } | null {
  const rent = invoices.filter(isRentInvoice)
  const time = (value: string) => new Date(value).getTime()

  const unpaid = rent.filter(isPayable)
  if (unpaid.length > 0) {
    return {
      from: unpaid.reduce((min, invoice) => (time(invoice.from) < time(min) ? invoice.from : min), unpaid[0].from),
      to: unpaid.reduce((max, invoice) => (time(invoice.to) > time(max) ? invoice.to : max), unpaid[0].to),
    }
  }

  // A move's `settled` invoice is covered by money carried over: for the period it counts as paid.
  const lastPaid = rent
    .filter((invoice) => invoice.status === "paid" || invoice.status === "settled")
    .reduce<Invoice | undefined>((last, invoice) => (!last || time(invoice.to) > time(last.to) ? invoice : last), undefined)
  return lastPaid ? { from: lastPaid.from, to: lastPaid.to } : null
}
